import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { PaperTitle } from "Components/Shared/PaperTitle";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { BankAccountStatus } from "Infrastructure/Api/Api";
import {
  getResourcePath,
  Resources,
  useResource,
} from "Translations/Resources";
import { formatAmount } from "Utils/CurrencyUtils";
import { Trans } from "react-i18next";

const RESOURCES = Resources.Pages.Content.Dashboard.PreApprovalWidget;

const WARNING_THRESHOLD_PERCENT = 80;

export const PreApprovalWidget = () => {
  const { t } = useResource();
  const { data, isLoading } = useBankingAccounts();

  if (isLoading) {
    return null;
  }

  const limits = data?.items?.find(
    ({ status }) => status === BankAccountStatus.PreApproved,
  )?.paymentLimits;

  if (!limits?.hasLimit) {
    return null;
  }

  const total = limits.preApprovalTotalLimit.value;
  const remaining = limits.preApprovalRemainingAmount.value;

  const used = total - remaining;
  const usedPercent =
    total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  const isNearLimit = usedPercent >= WARNING_THRESHOLD_PERCENT;

  return (
    <PrimaryPaper>
      <Stack gap={1.5}>
        <PaperTitle>{t(RESOURCES.Title)}</PaperTitle>
        <Stack gap={4}>
          <Stack gap={1}>
            <Typography
              variant="body2"
              fontSize="0.875rem"
              color="text.secondary"
            >
              {t(RESOURCES.Description)}
            </Typography>
            <Stack
              component="ul"
              gap={0.5}
              mb={0}
              ml={2}
              mt={0.5}
              pl={2.5}
              sx={{ listStyleType: "disc" }}
            >
              <Typography
                component="li"
                variant="body2"
                fontSize="0.875rem"
                color="text.secondary"
              >
                <Trans
                  i18nKey={getResourcePath(RESOURCES.LimitPerTransaction)}
                  components={{
                    amount: <Box component="span" fontWeight={600} />,
                  }}
                  values={{
                    amount: formatAmount(limits.preApprovalOneTimeLimit),
                  }}
                />
              </Typography>
              <Typography
                component="li"
                variant="body2"
                fontSize="0.875rem"
                color="text.secondary"
              >
                <Trans
                  i18nKey={getResourcePath(RESOURCES.LimitTotal)}
                  components={{
                    amount: <Box component="span" fontWeight={600} />,
                  }}
                  values={{
                    amount: formatAmount(limits.preApprovalTotalLimit),
                  }}
                />
              </Typography>
            </Stack>
          </Stack>
          <Box>
            <LinearProgress
              variant="determinate"
              value={usedPercent}
              sx={{
                height: 10,
                borderRadius: 4,
                backgroundColor: "grey.200",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 4,
                  backgroundColor: isNearLimit ? "error.main" : "primary.main",
                },
              }}
            />
            <Typography
              variant="body1"
              fontWeight={400}
              mt={1}
              textAlign="right"
              color={isNearLimit ? "error.main" : "text.primary"}
            >
              {t(RESOURCES.Remaining, {
                remaining: formatAmount(limits.preApprovalRemainingAmount),
              })}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    </PrimaryPaper>
  );
};
